import React, { useState, useEffect } from "react";
import { DEFAULT_BATCH_YEAR, BATCH_OPTIONS } from "../../config";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Table, Alert } from "@mui/material";
import Banner from "../Common/Banner";
import { RotatingTriangles } from "react-loader-spinner";
import SnackbarComponent from "../Common/SnackbarComponent";
import api from "../Auth/Middleware";
import { useNavigate } from "react-router-dom";

const AddScore = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [testDate, setTestDate] = useState("");
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scores, setScores] = useState([]);
  const [snackbarMsg, setSnackbarMSg] = useState("");
  const [snackbarSevierty, setSnackbarSevierty] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [maxMarks, setMaxMarks] = useState(100);
  const role = sessionStorage.getItem("role");
  const [batch, setBatch] = useState(
    role === "Admin"
      ? (parseInt(sessionStorage.getItem("selectedBatch")) || DEFAULT_BATCH_YEAR)
      : DEFAULT_BATCH_YEAR
  );
  const [batchEnrollmentId, setBatchEnrollmentId] = useState(null);

  const handleBatchChange = (e) => {
    setBatch(e.target.value);
    sessionStorage.setItem("selectedBatch", e.target.value);
  };
  const [noStudentsFound, setNoStudentsFound] = useState(false);
  const navigate = useNavigate();

  const classes = [
    { name: "5th", subjects: ["Maths", "Science", "English"] },
    { name: "6th", subjects: ["Maths", "Science", "English"] },
    { name: "7th", subjects: ["Maths", "Science", "English"] },
    { name: "8th", subjects: ["Maths", "Science", "English"] },
    { name: "9th", subjects: ["Maths", "Science", "English"] },
    { name: "10th", subjects: ["Maths", "Science", "English"] },
    { name: "11th", subjects: ["Maths", "Physics", "Chemistry"] },
    { name: "12th", subjects: ["Maths", "Physics", "Chemistry"] },
  ];

  const SubjectsEnum = {
    Maths: 1,
    Science: 2,
    Physics: 3,
    Chemistry: 4,
    English: 5,
  };

  const handleClassChange = (event) => {
    const { value } = event.target;
    setSelectedClass(value);
    setSelectedSubject("");
  };

  const handleSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
  };

  const handleTestDateChange = (event) => {
    setTestDate(event.target.value);
  };

  const handleScoreChange = (studentId, mark) => {
    const student = students.find((s) => s.id === studentId);

    if (student) {
      if (mark >= 0 && mark <= maxMarks) {
        const updatedScores = scores.map((s) =>
          s.studentId === studentId ? { ...s, mark } : s
        );
        setScores(updatedScores);
      } else {
        setSnackbarOpen(true);
        setSnackbarMSg(
          "Invalid mark entered. Marks should be between 0 and " + maxMarks
        );
        setSnackbarSevierty("warning");
      }
    }
  };

  const handleAbsentToggle = (studentId, checked) => {
    setScores((prev) =>
      prev.map((s) =>
        s.studentId === studentId ? { ...s, mark: checked ? -1 : 0 } : s
      )
    );
  };

  const handleMaxMarksChange = (event) => {
    setMaxMarks(event.target.value);
  };

  useEffect(() => {
    // Fetch students data from API
    if (selectedClass && selectedSubject && testDate) {
      setIsLoading(true);
      setNoStudentsFound(false);
      const fetchData = async () => {
        try {
          const response = await api.get(
            `Student/StudentDetails?class=${selectedClass.slice(
              0,
              -2
            )}&course=${SubjectsEnum[selectedSubject]}&date=${testDate}&batch=${batch}`
          );
          const fetched = response.data.students ?? [];
          setStudents(fetched);
          if (fetched.length === 0) {
            setNoStudentsFound(true);
          } else {
            setMaxMarks(
              fetched[0]?.maxMarks !== 0 ? fetched[0]?.maxMarks : 100
            );
            const defaultScores = fetched.map((student) => ({
              id: student.marksId !== 0 ? student.marksId : null,
              studentId: student.id,
              mark: student.marks,
            }));
            setScores(defaultScores);
          }
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          setSnackbarOpen(true);
          setSnackbarMSg("Something went Wrong!! Please refresh the page");
          setSnackbarSevierty("error");
        }
      };
      fetchData();
    } else {
      setNoStudentsFound(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSubject, testDate, batch]);

  // Fetch batchEnrollmentId from AllStudent when class or batch changes.
  // StudentDetails does not return batchEnrollmentId; AllStudent does.
  useEffect(() => {
    if (!selectedClass || !batch) {
      setBatchEnrollmentId(null);
      return;
    }
    const classNumber = parseInt(selectedClass.slice(0, -2));
    const fetchBatchEnrollmentId = async () => {
      try {
        const response = await api.get(`student/AllStudent?batch=${batch}`);
        const studentInClass = response.data?.students?.find(
          (s) => s.class === classNumber
        );
        setBatchEnrollmentId(studentInClass?.batchEnrollmentId ?? null);
      } catch {
        setBatchEnrollmentId(null);
      }
    };
    fetchBatchEnrollmentId();
  }, [selectedClass, batch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!batchEnrollmentId) {
      setSnackbarOpen(true);
      setSnackbarMSg("Batch enrollment ID not found. Please ensure students are enrolled for this batch and class.");
      setSnackbarSevierty("error");
      return;
    }
    const patchData = {
      class: +selectedClass.slice(0, -2),
      course: SubjectsEnum[selectedSubject],
      dateOfExam: testDate,
      marks: scores,
      maxMarks: maxMarks,
      batchEnrollmentId: batchEnrollmentId,
    };
    try {
      const response = await api.patch("Student/UpdateMarks", patchData);
      if (response.status === 200) {
        handleReset();
        setSnackbarOpen(true);
        setSnackbarMSg(`Marks Updated Successfully for Class ${selectedClass}`);
        setSnackbarSevierty("success");
        navigate('/teacherDashboard');
        
      } else {
        setSnackbarOpen(true);
        setSnackbarMSg(`Something Went Wrong! Please try saving again`);
        setSnackbarSevierty("success");
      }
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("API Error Occured");
      setSnackbarSevierty("error");
    }
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleReset = () => {
    setSelectedClass("");
    setSelectedSubject("");
    setTestDate("");
    setStudents([]);
    setScores([]);
    setNoStudentsFound(false);
  };

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        severity={snackbarSevierty}
        message={snackbarMsg}
        onClose={closeSnackbar}
      ></SnackbarComponent>
      <Banner
        title="Add Score"
        currentpage="Add Score"
        previousPage="Teacher Dashboard"
        link="/teacherDashboard"
      />
      <div className="container my-5">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 col-sm-12">
              <Grid container spacing={2}>
                {role === "Admin" && (
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="batch-label">Batch Year</InputLabel>
                      <Select
                        labelId="batch-label"
                        label="Batch Year"
                        value={batch}
                        onChange={handleBatchChange}
                      >
                        {BATCH_OPTIONS.map((b) => (
                          <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="class">Class</InputLabel>
                    <Select
                      label="Class"
                      value={selectedClass}
                      onChange={handleClassChange}
                    >
                      {classes.map((cls) => (
                        <MenuItem key={cls.name} value={cls.name}>
                          {cls.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="subject">Subject</InputLabel>
                    <Select
                      label="Subject"
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      disabled={!selectedClass}
                    >
                      {selectedClass &&
                        classes
                          .find((cls) => cls.name === selectedClass)
                          .subjects.map((subject) => (
                            <MenuItem key={subject} value={subject}>
                              {subject}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Test"
                    value={testDate}
                    onChange={handleTestDateChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={!selectedClass || !selectedSubject}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Maximum Marks"
                    value={maxMarks}
                    onChange={handleMaxMarksChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={!selectedClass || !selectedSubject || !testDate}
                  />
                </Grid>
              </Grid>
            </div>
            <div className="col-md-2"></div>
            <div className="col-md-6 col-sm-12">
              <div className="text-center">
                {isLoading && <RotatingTriangles className="ml-2" />}
              </div>
              {!isLoading && noStudentsFound && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No students enrolled in <strong>{selectedClass} — {selectedSubject}</strong> for batch <strong>{batch}</strong>.
                </Alert>
              )}
              {students.length > 0 && (
                <div>
                  <Table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Score</th>
                        <th>Absent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => {
                        const currentMark = scores.find((s) => s.studentId === student.id)?.mark;
                        const isAbsent = currentMark === -1;
                        return (
                          <tr key={student.id}>
                            <td>{student.firstName + " " + student.lastName}</td>
                            <td>
                              <input
                                className="form-control"
                                type="number"
                                value={isAbsent ? "" : (currentMark ?? "")}
                                onChange={(e) =>
                                  handleScoreChange(student.id, +e.target.value)
                                }
                                disabled={isLoading || isAbsent}
                              />
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={isAbsent}
                                onChange={(e) =>
                                  handleAbsentToggle(student.id, e.target.checked)
                                }
                                disabled={isLoading}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <Button
                    className="my-5"
                    variant="contained"
                    type="submit"
                    disabled={isLoading || !students.length}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddScore;
